import { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { toast } from "react-toastify";
import downloadIcon from "../../../../assets/images/download.svg";
import whiteDownloadIcon from "../../../../assets/images/white-download.svg";
import whiteImageIcon from "../../../../assets/images/white-image-icon.svg";
import blackImageIcon from "../../../../assets/images/black-image-icon.svg";
import whitePdfIcon from "../../../../assets/images/white-pdf-icon.svg";
import blackPdfIcon from "../../../../assets/images/black-pdf-icon.svg";
import whiteDocxIcon from "../../../../assets/images/white-docx-icon.svg";
import blackDocxIcon from "../../../../assets/images/black-docx-icon.svg";
import NoImage from "../../../../assets/images/no-image-avatar.png";
import { IMAGE_URL } from "../../../../app_url";
import { chatMessgaeDateFormat, getFileExtension } from "../../../../utils/commonData";
import { fetchDoctorDictionaryMessageDetailsApi } from "../../../../services/apiService";
import "./chat-with-doctor-card.css";

const DoctorChatSkeleton = () => (
  <li className="doctor-chat-skeleton" data-testid="doctor-chat-skeleton" aria-label="Loading doctor messages">
    {[0, 1, 2].map((item) => (
      <div className="doctor-chat-skeleton__message" key={item}>
        <Skeleton circle width={38} height={38} />
        <div className="doctor-chat-skeleton__content">
          <Skeleton width={135} height={12} />
          <Skeleton width={item === 1 ? "68%" : "84%"} height={38} />
        </div>
      </div>
    ))}
  </li>
);

const MessageFileIcon = ({ fileName, outgoing }) => {
  const extension = getFileExtension(fileName);
  const icon = extension === "pdf"
    ? (outgoing ? whitePdfIcon : blackPdfIcon)
    : extension === "docx"
      ? (outgoing ? whiteDocxIcon : blackDocxIcon)
      : (outgoing ? whiteImageIcon : blackImageIcon);
  return <img src={icon} className="downloadIcon normalIcon" alt="" />;
};

export default function ChatWithDoctorCard({ activeIndexes, toggleAccordion, patientId, DOC_doctorId, userData }) {
  const chatListRef = useRef(null);
  const [doctorMessages, setDoctorMessages] = useState([]);
  const [loadedPatientId, setLoadedPatientId] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const isExpanded = activeIndexes.includes(1);

  useEffect(() => {
    let isCurrent = true;
    const loadDoctorMessages = async () => {
      if (!patientId) {
        setDoctorMessages([]);
        setLoadedPatientId(null);
        return;
      }
      if (!isExpanded || loadedPatientId === patientId) return;

      try {
        setDoctorMessages([]);
        setMessagesLoading(true);
        const formData = new FormData();
        formData.append("patient_id", patientId);
        if (DOC_doctorId) formData.append("doctor_id", DOC_doctorId);
        const response = await fetchDoctorDictionaryMessageDetailsApi(formData);
        if (!isCurrent) return;
        if (response?.data?.res === true) {
          setDoctorMessages(response.data.data || []);
          setLoadedPatientId(patientId);
        } else {
          toast.error(response?.data?.msg || "Something went wrong in server. Please try again.", { autoClose: 1500 });
        }
      } catch (error) {
        if (!isCurrent) return;
        console.error(error);
        toast.error("Something went wrong in server. Please try again.", { autoClose: 1500 });
      } finally {
        if (isCurrent) setMessagesLoading(false);
      }
    };
    loadDoctorMessages();
    return () => { isCurrent = false; };
  }, [DOC_doctorId, isExpanded, loadedPatientId, patientId]);

  useEffect(() => {
    if (chatListRef.current) chatListRef.current.scrollTop = chatListRef.current.scrollHeight;
  }, [doctorMessages]);

  const handleDownload = async (fileUrl, fileName) => {
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Network response was not ok");
      const blobUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Downloaded successfully", { autoClose: 1500 });
    } catch (error) {
      console.error("Error downloading the file:", error);
      toast.error("Failed to download the file.", { autoClose: 1500 });
    }
  };

  const visibleMessages = doctorMessages.filter((item) =>
    parseInt(item?.from_id) === parseInt(DOC_doctorId) || parseInt(item?.to_id) === parseInt(DOC_doctorId),
  );

  return (
    <div className="accordion-card">
      <div
        className={`accordion-header accordion-header1 searchDoctorAccordian ${isExpanded ? "active" : ""}`}
        onClick={() => toggleAccordion(1)}
      >
        Chat with Doctors
      </div>
      {isExpanded && (
        <div className="accordion-content">
          <div className="PatientsChatCard PatientsChatCardPfl doctorMessage"><div className="BottomChatBox">
            <ul className="ChatList" ref={chatListRef}>
              {messagesLoading ? <DoctorChatSkeleton /> : visibleMessages.length > 0 ? visibleMessages.map((item, index) => {
                const outgoing = item?.from_id === userData?.users?.id;
                return (
                  <li key={item?.id || `${item?.date}-${index}`} className={outgoing ? "UserList" : ""}>
                    <div className="ChatInfoTop"><div className="ChatUserImg"><img src={item?.from_profile_pic_url || NoImage} className="profileImg" alt={item?.from || "Doctor"} /></div>
                      <div className="ChatUserInfo"><h4>
                        {outgoing ? "You" : item?.from || "Doctor"}
                        {item?.link_patient_name ? (
                          <> | <span className={item?.types === "refer" ? "DoctorNotExist" : "aboutPatientSpan"}>
                            {item?.types === "refer" ? "Referrel patient" : "About patient"} {item.link_patient_name}
                          </span></>
                        ) : null}
                      </h4><h5>{chatMessgaeDateFormat(item?.date)}</h5>
                      </div>
                    </div>
                    {item?.message && <div className={`DoctorChatContent ${outgoing ? "DoctorChatContent2" : ""}`}>{item.message}</div>}
                    {item?.file_name && item?.files && (
                      <div className={`DoctorChatContent fileSection ${outgoing ? "DoctorChatContent2" : ""}`}>
                        <MessageFileIcon fileName={item.file_name} outgoing={outgoing} /><span>{item.file_name}</span>
                        <img src={outgoing ? whiteDownloadIcon : downloadIcon} className="downloadIcon" alt="downloadIcon" onClick={() => handleDownload(IMAGE_URL + item.files, item.file_name)} />
                      </div>
                    )}
                  </li>
                );
              }) : (
                <li className="EmptyChatSection"><div className="DoctorChatContent">Conversation is empty.</div></li>
              )}
            </ul>
          </div></div>
        </div>
      )}
    </div>
  );
}
